import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import VideocamIcon from "@mui/icons-material/Videocam";
import DashboardLayout from "../layouts/DashboardLayout";
import api, { TOKEN_STORAGE_KEY } from "../api/client";
import { INCIDENT_TYPES, SERVICE_OPTIONS } from "../constants/incidentOptions";
import { AuthContext } from "../context/AuthContext";

// Format incident type for display: "assist-motorist" -> "ASSIST MOTORIST"
const formatIncidentType = (type) => {
  if (!type) return "—";
  return type.replace(/-/g, ' ').toUpperCase();
};

// Build a URL with authentication token for video/image elements
const buildSecuredUrl = (rawUrl) => {
  if (!rawUrl) return "";
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (!token) return rawUrl;
  const separator = rawUrl.includes("?") ? "&" : "?";
  return `${rawUrl}${separator}access_token=${encodeURIComponent(token)}`;
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 75, 100];

const normalizeRangerId = (value) => (value ? value.trim().toUpperCase() : "");

const formatDateTime = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

const formatDuration = (minutes) => {
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return "—";
  }

  const rounded = Math.round(minutes);
  const hours = Math.floor(rounded / 60);
  const remaining = rounded % 60;
  const parts = [];

  if (hours > 0) {
    parts.push(`${hours}h`);
  }

  if (remaining > 0 || parts.length === 0) {
    parts.push(`${remaining}m`);
  }

  return parts.join(" ");
};

const computeDurationMinutes = (incident) => {
  if (!incident) {
    return null;
  }

  const value = Number(incident.durationMinutes);
  if (Number.isFinite(value) && value > 0) {
    return value;
  }

  const start = incident.occurredAtUtc ? new Date(incident.occurredAtUtc) : null;
  const end = incident.occurredEndUtc ? new Date(incident.occurredEndUtc) : null;

  if (!start || Number.isNaN(start.getTime()) || !end || Number.isNaN(end.getTime())) {
    return null;
  }

  const diffMinutes = (end.getTime() - start.getTime()) / 60000;
  return diffMinutes > 0 ? diffMinutes : null;
};

const resolvePrimaryRanger = (incident) => {
  if (!incident) {
    return "";
  }

  if (incident.primaryRangerId) {
    return normalizeRangerId(incident.primaryRangerId);
  }

  if (Array.isArray(incident.rangerIds) && incident.rangerIds.length > 0) {
    return normalizeRangerId(incident.rangerIds[0]);
  }

  return "";
};

const resolveDisposition = (incident) => {
  if (!incident) {
    return "";
  }

  if (Array.isArray(incident.servicesProvided) && incident.servicesProvided.length > 0) {
    return incident.servicesProvided
      .map((service) => (service || "").trim().toUpperCase())
      .filter(Boolean)
      .join(", ");
  }

  if (incident.needsVideo) {
    return "VIDEO REQUIRED";
  }

  return "";
};

const buildIsoBoundary = (value, boundary) => {
  if (!value) {
    return undefined;
  }

  const suffix = boundary === "end" ? "T23:59:59" : "T00:00:00";
  const date = new Date(`${value}${suffix}`);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const compareIncidents = (a, b, field) => {
  if (field === "occurredAtUtc") {
    const aTime = a.occurredAtUtc ? new Date(a.occurredAtUtc).getTime() : 0;
    const bTime = b.occurredAtUtc ? new Date(b.occurredAtUtc).getTime() : 0;
    return aTime - bTime;
  }

  if (field === "primaryRangerId") {
    return resolvePrimaryRanger(a).localeCompare(resolvePrimaryRanger(b));
  }

  if (field === "incidentType") {
    return (a.incidentType || "").toUpperCase().localeCompare((b.incidentType || "").toUpperCase());
  }

  if (field === "disposition") {
    return resolveDisposition(a).localeCompare(resolveDisposition(b));
  }

  return 0;
};

function SupervisorIncidentHistory() {
  const { user } = useContext(AuthContext);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filterError, setFilterError] = useState("");
  const [filters, setFilters] = useState({
    rangerId: "",
    incidentType: "",
    disposition: "",
    fromDate: "",
    toDate: "",
    hasVideo: false,
  });
  // Applied filters are what actually get sent to the server
  const [appliedFilters, setAppliedFilters] = useState({
    rangerId: "",
    incidentType: "",
    disposition: "",
    hasVideo: false,
  });
  const [sortConfig, setSortConfig] = useState({ field: "occurredAtUtc", direction: "desc" });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [total, setTotal] = useState(0);
  const [dateRange, setDateRange] = useState({ fromUtc: undefined, toUtc: undefined });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, incident: null, submitting: false, error: "" });
  const [editDialog, setEditDialog] = useState({
    open: false,
    incident: null,
    incidentType: "",
    primaryRangerId: "",
    notes: "",
    servicesProvided: [],
    occurredAtUtc: "",
    occurredEndUtc: "",
    submitting: false,
    error: "",
  });
  const [backdatedDialog, setBackdatedDialog] = useState({
    open: false,
    incidentType: "",
    primaryRangerId: "",
    notes: "",
    servicesProvided: [],
    occurredAtUtc: "",
    occurredEndUtc: "",
    latitude: "",
    longitude: "",
    locationDescription: "",
    submitting: false,
    error: "",
    success: null,
  });
  const [videoDialog, setVideoDialog] = useState({
    open: false,
    incident: null,
    videos: [],
    selectedVideoId: null,
  });
  const [deleteVideoDialog, setDeleteVideoDialog] = useState({
    open: false,
    video: null,
    submitting: false,
    error: "",
  });

  const isAdmin = useMemo(() => (user?.role || "").toLowerCase() === "admin", [user?.role]);
  const isSupervisorOrAdmin = useMemo(() => {
    const role = (user?.role || "").toLowerCase();
    return role === "admin" || role === "supervisor";
  }, [user?.role]);

  const fetchIncidents = useCallback(async (pageNumber, size, range, currentFilters) => {
    setLoading(true);
    setError("");
    const requestPage = pageNumber + 1;

    try {
      const params = { page: requestPage, pageSize: size };
      if (range?.fromUtc) {
        params.fromUtc = range.fromUtc;
      }
      if (range?.toUtc) {
        params.toUtc = range.toUtc;
      }
      if (currentFilters?.hasVideo) {
        params.hasVideo = true;
      }
      // Server-side filters
      if (currentFilters?.rangerId?.trim()) {
        params.rangerId = currentFilters.rangerId.trim();
      }
      if (currentFilters?.incidentType?.trim()) {
        params.incidentType = currentFilters.incidentType.trim();
      }
      if (currentFilters?.disposition?.trim()) {
        params.disposition = currentFilters.disposition.trim();
      }

      console.log('Fetching incidents with params:', params);
      const response = await api.get("/incidents", { params });
      const payload = response.data || {};
      const items = Array.isArray(payload.items) ? payload.items : [];
      const reportedTotal = Number(payload.total);
      console.log('Received', items.length, 'of', reportedTotal, 'total incidents');

      setIncidents(items);
      setTotal(Number.isFinite(reportedTotal) && reportedTotal >= 0 ? reportedTotal : items.length);
    } catch (err) {
      console.error(err);
      setError("Unable to load incident history right now.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIncidents(page, pageSize, dateRange, appliedFilters);
  }, [dateRange, fetchIncidents, page, pageSize, appliedFilters.rangerId, appliedFilters.incidentType, appliedFilters.disposition, appliedFilters.hasVideo]);

  const handleFilterChange = (field) => (event) => {
    const { value } = event.target;
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleHasVideoChange = (event) => {
    const checked = event.target.checked;
    setFilters((prev) => ({ ...prev, hasVideo: checked }));
    // Apply immediately for hasVideo toggle
    setAppliedFilters((prev) => ({ ...prev, hasVideo: checked }));
    setPage(0);
  };

  const handleApplyFilters = () => {
    if (filters.fromDate && filters.toDate && filters.toDate < filters.fromDate) {
      setFilterError("End date must be on or after the start date.");
      return;
    }

    setFilterError("");
    setDateRange({
      fromUtc: buildIsoBoundary(filters.fromDate, "start"),
      toUtc: buildIsoBoundary(filters.toDate, "end"),
    });
    // Apply all filters to server-side query
    setAppliedFilters({
      rangerId: filters.rangerId,
      incidentType: filters.incidentType,
      disposition: filters.disposition,
      hasVideo: filters.hasVideo,
    });
    setPage(0);
  };

  const handleResetFilters = () => {
    setFilters({ rangerId: "", incidentType: "", disposition: "", fromDate: "", toDate: "", hasVideo: false });
    setAppliedFilters({ rangerId: "", incidentType: "", disposition: "", hasVideo: false });
    setFilterError("");
    setDateRange({ fromUtc: undefined, toUtc: undefined });
    setPage(0);
  };

  const handlePageChange = (_event, newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (event) => {
    const nextSize = Number(event.target.value) || PAGE_SIZE_OPTIONS[0];
    setPageSize(nextSize);
    setPage(0);
  };

  const openDeleteDialog = (incident) => {
    setDeleteDialog({ open: true, incident, submitting: false, error: "" });
  };

  const closeDeleteDialog = (force = false) => {
    setDeleteDialog((prev) =>
      prev.submitting && !force ? prev : { open: false, incident: null, submitting: false, error: "" }
    );
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog.incident || deleteDialog.submitting) {
      return;
    }

    setDeleteDialog((prev) => ({ ...prev, submitting: true, error: "" }));

    try {
      await api.delete(`/incidents/${deleteDialog.incident.id}`);

      const shouldMoveBack = incidents.length === 1 && page > 0;
      const targetPage = shouldMoveBack ? page - 1 : page;

      closeDeleteDialog(true);

      if (shouldMoveBack) {
        setPage(targetPage);
      } else {
        fetchIncidents(targetPage, pageSize, dateRange, appliedFilters);
      }
    } catch (err) {
      console.error(err);
      const message = err.response?.data ?? "Unable to delete this incident right now.";
      setDeleteDialog((prev) => ({ ...prev, submitting: false, error: typeof message === "string" ? message : "Unable to delete this incident right now." }));
    }
  };

  // Video Dialog Handlers
  const openVideoDialog = async (incident) => {
    try {
      const response = await api.get(`/incidents/${incident.id}`);
      const videos = Array.isArray(response.data?.videos) ? response.data.videos : [];
      setVideoDialog({
        open: true,
        incident,
        videos,
        selectedVideoId: videos.length > 0 ? videos[0].id : null,
      });
    } catch (err) {
      console.error("Failed to load incident videos:", err);
    }
  };

  const closeVideoDialog = () => {
    setVideoDialog({ open: false, incident: null, videos: [], selectedVideoId: null });
  };

  const handleSelectVideo = (videoId) => {
    setVideoDialog((prev) => ({ ...prev, selectedVideoId: videoId }));
  };

  // Delete Video Dialog Handlers
  const openDeleteVideoDialog = (video) => {
    setDeleteVideoDialog({ open: true, video, submitting: false, error: "" });
  };

  const closeDeleteVideoDialog = (force = false) => {
    setDeleteVideoDialog((prev) =>
      prev.submitting && !force ? prev : { open: false, video: null, submitting: false, error: "" }
    );
  };

  const handleConfirmDeleteVideo = async () => {
    if (!deleteVideoDialog.video || deleteVideoDialog.submitting) return;

    setDeleteVideoDialog((prev) => ({ ...prev, submitting: true, error: "" }));

    try {
      await api.delete(`/videos/${deleteVideoDialog.video.id}`);
      
      // Update video dialog to remove deleted video
      setVideoDialog((prev) => {
        const updatedVideos = prev.videos.filter((v) => v.id !== deleteVideoDialog.video.id);
        return {
          ...prev,
          videos: updatedVideos,
          selectedVideoId: updatedVideos.length > 0 ? updatedVideos[0].id : null,
        };
      });

      // Refresh incidents to update video count
      fetchIncidents(page, pageSize, dateRange, appliedFilters);
      closeDeleteVideoDialog(true);
    } catch (err) {
      console.error(err);
      const message = err.response?.data ?? "Unable to delete video.";
      setDeleteVideoDialog((prev) => ({
        ...prev,
        submitting: false,
        error: typeof message === "string" ? message : "Unable to delete video.",
      }));
    }
  };

  // Convert UTC to local datetime-local input value
  const toLocalDatetimeInput = (utcString) => {
    if (!utcString) return "";
    const date = new Date(utcString);
    if (Number.isNaN(date.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  // Convert local datetime-local input to ISO string
  const fromLocalDatetimeInput = (localString) => {
    if (!localString) return null;
    const date = new Date(localString);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString();
  };

  const openEditDialog = (incident) => {
    setEditDialog({
      open: true,
      incident,
      incidentType: incident.incidentType || "",
      primaryRangerId: incident.primaryRangerId || "",
      notes: incident.notes || "",
      servicesProvided: Array.isArray(incident.servicesProvided) ? incident.servicesProvided : [],
      occurredAtUtc: toLocalDatetimeInput(incident.occurredAtUtc),
      occurredEndUtc: toLocalDatetimeInput(incident.occurredEndUtc),
      submitting: false,
      error: "",
    });
  };

  const closeEditDialog = (force = false) => {
    setEditDialog((prev) =>
      prev.submitting && !force
        ? prev
        : {
            open: false,
            incident: null,
            incidentType: "",
            primaryRangerId: "",
            notes: "",
            servicesProvided: [],
            occurredAtUtc: "",
            occurredEndUtc: "",
            submitting: false,
            error: "",
          }
    );
  };

  const handleEditFieldChange = (field) => (event) => {
    const value = event.target.value;
    setEditDialog((prev) => ({ ...prev, [field]: value }));
  };

  const handleServicesChange = (event) => {
    const value = event.target.value;
    setEditDialog((prev) => ({
      ...prev,
      servicesProvided: typeof value === "string" ? value.split(",") : value,
    }));
  };

  const handleConfirmEdit = async () => {
    if (!editDialog.incident || editDialog.submitting) return;

    setEditDialog((prev) => ({ ...prev, submitting: true, error: "" }));

    try {
      const payload = {
        incidentType: editDialog.incidentType || undefined,
        primaryRangerId: editDialog.primaryRangerId || undefined,
        notes: editDialog.notes || undefined,
        servicesProvided: editDialog.servicesProvided,
        occurredAtUtc: fromLocalDatetimeInput(editDialog.occurredAtUtc),
        occurredEndUtc: fromLocalDatetimeInput(editDialog.occurredEndUtc),
      };

      await api.put(`/incidents/${editDialog.incident.id}`, payload);
      closeEditDialog(true);
      fetchIncidents(page, pageSize, dateRange, appliedFilters);
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.message || err.response?.data || "Unable to update this incident.";
      setEditDialog((prev) => ({
        ...prev,
        submitting: false,
        error: typeof message === "string" ? message : "Unable to update this incident.",
      }));
    }
  };

  // Backdated Incident Dialog Handlers
  const openBackdatedDialog = () => {
    setBackdatedDialog({
      open: true,
      incidentType: "",
      primaryRangerId: "",
      notes: "",
      servicesProvided: [],
      occurredAtUtc: "",
      occurredEndUtc: "",
      latitude: "",
      longitude: "",
      locationDescription: "",
      submitting: false,
      error: "",
      success: null,
    });
  };

  const closeBackdatedDialog = (force = false) => {
    setBackdatedDialog((prev) =>
      prev.submitting && !force
        ? prev
        : {
            open: false,
            incidentType: "",
            primaryRangerId: "",
            notes: "",
            servicesProvided: [],
            occurredAtUtc: "",
            occurredEndUtc: "",
            latitude: "",
            longitude: "",
            locationDescription: "",
            submitting: false,
            error: "",
            success: null,
          }
    );
  };

  const handleBackdatedFieldChange = (field) => (event) => {
    const value = event.target.value;
    setBackdatedDialog((prev) => ({ ...prev, [field]: value, error: "", success: null }));
  };

  const handleBackdatedServicesChange = (event) => {
    const value = event.target.value;
    setBackdatedDialog((prev) => ({
      ...prev,
      servicesProvided: typeof value === "string" ? value.split(",") : value,
      error: "",
      success: null,
    }));
  };

  const handleConfirmBackdated = async () => {
    if (backdatedDialog.submitting) return;

    // Validation
    if (!backdatedDialog.incidentType) {
      setBackdatedDialog((prev) => ({ ...prev, error: "Incident type is required." }));
      return;
    }
    if (!backdatedDialog.occurredAtUtc) {
      setBackdatedDialog((prev) => ({ ...prev, error: "Start time is required." }));
      return;
    }

    const occurredAt = fromLocalDatetimeInput(backdatedDialog.occurredAtUtc);
    if (!occurredAt) {
      setBackdatedDialog((prev) => ({ ...prev, error: "Invalid start time." }));
      return;
    }

    // Check if the time is in the past
    if (new Date(occurredAt) > new Date()) {
      setBackdatedDialog((prev) => ({ ...prev, error: "Start time must be in the past for a backdated incident." }));
      return;
    }

    setBackdatedDialog((prev) => ({ ...prev, submitting: true, error: "", success: null }));

    try {
      const payload = {
        incidentType: backdatedDialog.incidentType,
        occurredAtUtc: occurredAt,
        occurredEndUtc: fromLocalDatetimeInput(backdatedDialog.occurredEndUtc),
        latitude: backdatedDialog.latitude ? parseFloat(backdatedDialog.latitude) : undefined,
        longitude: backdatedDialog.longitude ? parseFloat(backdatedDialog.longitude) : undefined,
        primaryRangerId: backdatedDialog.primaryRangerId || undefined,
        notes: backdatedDialog.notes || undefined,
        servicesProvided: backdatedDialog.servicesProvided,
        locationDescription: backdatedDialog.locationDescription || undefined,
      };

      const response = await api.post("/incidents/backdated", payload);
      const data = response.data;
      
      const cadId = data.incident?.cadId || "Unknown";
      const renumberedCount = data.renumberedCount || 0;
      
      let successMsg = `Created backdated incident ${cadId}.`;
      if (renumberedCount > 0) {
        successMsg += ` ${renumberedCount} subsequent incident${renumberedCount === 1 ? " was" : "s were"} renumbered.`;
      }

      setBackdatedDialog((prev) => ({
        ...prev,
        submitting: false,
        success: successMsg,
      }));

      // Refresh the list
      fetchIncidents(page, pageSize, dateRange, appliedFilters);
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.message || err.response?.data || "Unable to create backdated incident.";
      setBackdatedDialog((prev) => ({
        ...prev,
        submitting: false,
        error: typeof message === "string" ? message : "Unable to create backdated incident.",
      }));
    }
  };

  // All filtering is now done server-side, so sortedIncidents works directly with incidents
  const sortedIncidents = useMemo(() => {
    const rows = incidents.slice();
    rows.sort((a, b) => compareIncidents(a, b, sortConfig.field));

    if (sortConfig.direction === "desc") {
      rows.reverse();
    }

    return rows;
  }, [incidents, sortConfig.direction, sortConfig.field]);

  const handleSort = (field) => () => {
    setSortConfig((prev) => {
      if (prev.field === field) {
        const nextDirection = prev.direction === "asc" ? "desc" : "asc";
        return { field, direction: nextDirection };
      }

      return { field, direction: "asc" };
    });
  };

  return (
    <DashboardLayout>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Incident History
          </Typography>
          <Typography color="text.secondary">
            Review every ranger incident, refine by filters, and sort key columns for targeted auditing.
          </Typography>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}
        {filterError && <Alert severity="warning">{filterError}</Alert>}

        <Paper sx={{ p: 3 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Ranger ID"
              value={filters.rangerId}
              onChange={handleFilterChange("rangerId")}
              placeholder="e.g. R123"
              fullWidth
            />
            <TextField
              select
              label="Incident Type"
              value={filters.incidentType}
              onChange={handleFilterChange("incidentType")}
              fullWidth
            >
              <MenuItem value="">All Types</MenuItem>
              {INCIDENT_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Disposition"
              value={filters.disposition}
              onChange={handleFilterChange("disposition")}
              fullWidth
            >
              <MenuItem value="">All Dispositions</MenuItem>
              {SERVICE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 2 }} alignItems="center">
            <TextField
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              value={filters.fromDate}
              onChange={handleFilterChange("fromDate")}
              fullWidth
            />
            <TextField
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              value={filters.toDate}
              onChange={handleFilterChange("toDate")}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={filters.hasVideo}
                  onChange={handleHasVideoChange}
                  color="primary"
                />
              }
              label="Has Video"
              sx={{ minWidth: 140 }}
            />
          </Stack>

          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
            {isSupervisorOrAdmin && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<AddIcon />}
                onClick={openBackdatedDialog}
                disabled={loading}
              >
                Create Backdated Incident
              </Button>
            )}
            <Button variant="outlined" onClick={handleResetFilters} disabled={loading}>
              Reset
            </Button>
            <Button variant="contained" onClick={handleApplyFilters} disabled={loading}>
              Apply Filters
            </Button>
          </Stack>
        </Paper>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary">
              Showing {sortedIncidents.length} of {total} incident{total === 1 ? "" : "s"}.
            </Typography>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>CAD</TableCell>
                    <TableCell sortDirection={sortConfig.field === "primaryRangerId" ? sortConfig.direction : false}>
                      <TableSortLabel
                        active={sortConfig.field === "primaryRangerId"}
                        direction={sortConfig.field === "primaryRangerId" ? sortConfig.direction : "asc"}
                        onClick={handleSort("primaryRangerId")}
                      >
                        Ranger ID
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={sortConfig.field === "incidentType" ? sortConfig.direction : false}>
                      <TableSortLabel
                        active={sortConfig.field === "incidentType"}
                        direction={sortConfig.field === "incidentType" ? sortConfig.direction : "asc"}
                        onClick={handleSort("incidentType")}
                      >
                        Incident Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={sortConfig.field === "disposition" ? sortConfig.direction : false}>
                      <TableSortLabel
                        active={sortConfig.field === "disposition"}
                        direction={sortConfig.field === "disposition" ? sortConfig.direction : "asc"}
                        onClick={handleSort("disposition")}
                      >
                        Disposition
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell sortDirection={sortConfig.field === "occurredAtUtc" ? sortConfig.direction : false}>
                      <TableSortLabel
                        active={sortConfig.field === "occurredAtUtc"}
                        direction={sortConfig.field === "occurredAtUtc" ? sortConfig.direction : "desc"}
                        onClick={handleSort("occurredAtUtc")}
                      >
                        Start Time
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>End Time</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Video</TableCell>
                    <TableCell>Location</TableCell>
                    {isSupervisorOrAdmin && <TableCell align="right">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedIncidents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isSupervisorOrAdmin ? 11 : 10} align="center" sx={{ py: 4 }}>
                        No incidents match the current filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedIncidents.map((incident) => {
                      const durationMinutes = computeDurationMinutes(incident);
                      const rangerId = resolvePrimaryRanger(incident) || "—";
                      const disposition = resolveDisposition(incident) || "—";

                      return (
                        <TableRow key={incident.id || incident.cadId} hover>
                          <TableCell>{incident.cadId || "—"}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {rangerId}
                            </Typography>
                            {Array.isArray(incident.rangerIds) && incident.rangerIds.length > 1 && (
                              <Typography variant="caption" color="text.secondary">
                                +{incident.rangerIds.length - 1} additional
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{formatIncidentType(incident.incidentType)}</TableCell>
                          <TableCell>{disposition}</TableCell>
                          <TableCell>
                            {incident.status ? (
                              <Chip
                                size="small"
                                label={incident.status.toUpperCase()}
                                color={(() => {
                                  const normalizedStatus = incident.status.toLowerCase();
                                  if (normalizedStatus === "open") {
                                    return "warning";
                                  }
                                  if (normalizedStatus === "cleared") {
                                    return "success";
                                  }
                                  if (normalizedStatus === "incomplete") {
                                    return "secondary";
                                  }
                                  return "default";
                                })()}
                              />
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell>{formatDateTime(incident.occurredAtUtc)}</TableCell>
                          <TableCell>{formatDateTime(incident.occurredEndUtc)}</TableCell>
                          <TableCell>{formatDuration(durationMinutes)}</TableCell>
                          <TableCell>
                            {incident.videoCount > 0 ? (
                              <Chip
                                size="small"
                                icon={<VideocamIcon />}
                                label={incident.videoCount}
                                color="info"
                                onClick={() => openVideoDialog(incident)}
                                sx={{ cursor: "pointer" }}
                              />
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell>{incident.location || incident.notes || "—"}</TableCell>
                          {isSupervisorOrAdmin && (
                            <TableCell align="right">
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Tooltip title="Edit Incident">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => openEditDialog(incident)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {isAdmin && (
                                  <Tooltip title="Delete Incident">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => openDeleteDialog(incident)}
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Stack>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handlePageSizeChange}
              rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            />
          </>
        )}

        <Dialog open={deleteDialog.open} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
          <DialogTitle>Delete Incident</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This permanently removes CAD {deleteDialog.incident?.cadId || ""}. This action cannot be undone.
            </DialogContentText>
            {deleteDialog.error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {deleteDialog.error}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDeleteDialog} disabled={deleteDialog.submitting}>
              Cancel
            </Button>
            <Button color="error" variant="contained" onClick={handleConfirmDelete} disabled={deleteDialog.submitting}>
              {deleteDialog.submitting ? "Deleting…" : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={editDialog.open} onClose={() => closeEditDialog()} maxWidth="sm" fullWidth>
          <DialogTitle>
            Edit Incident — CAD {editDialog.incident?.cadId || ""}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                select
                label="Incident Type"
                value={editDialog.incidentType}
                onChange={handleEditFieldChange("incidentType")}
                fullWidth
              >
                {INCIDENT_TYPES.map((type) => (
                  <MenuItem key={type} value={type.toLowerCase().replace(/\s+/g, "-")}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Primary Ranger ID"
                value={editDialog.primaryRangerId}
                onChange={handleEditFieldChange("primaryRangerId")}
                fullWidth
                placeholder="e.g. 121"
              />

              <TextField
                select
                SelectProps={{ multiple: true }}
                label="Services Provided"
                value={editDialog.servicesProvided}
                onChange={handleServicesChange}
                fullWidth
              >
                {SERVICE_OPTIONS.map((service) => (
                  <MenuItem key={service} value={service}>
                    {service}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Start Time"
                type="datetime-local"
                value={editDialog.occurredAtUtc}
                onChange={handleEditFieldChange("occurredAtUtc")}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />

              <TextField
                label="End Time"
                type="datetime-local"
                value={editDialog.occurredEndUtc}
                onChange={handleEditFieldChange("occurredEndUtc")}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />

              <TextField
                label="Notes"
                value={editDialog.notes}
                onChange={handleEditFieldChange("notes")}
                multiline
                rows={3}
                fullWidth
              />

              {editDialog.error && (
                <Alert severity="error">{editDialog.error}</Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => closeEditDialog()} disabled={editDialog.submitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmEdit}
              disabled={editDialog.submitting}
            >
              {editDialog.submitting ? "Saving…" : "Save Changes"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Backdated Incident Dialog */}
        <Dialog open={backdatedDialog.open} onClose={() => closeBackdatedDialog()} maxWidth="sm" fullWidth>
          <DialogTitle>Create Backdated Incident</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Create an incident that occurred in the past. This will automatically renumber any CAD IDs 
              for incidents that occurred after the specified time.
            </DialogContentText>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                select
                label="Incident Type"
                value={backdatedDialog.incidentType}
                onChange={handleBackdatedFieldChange("incidentType")}
                fullWidth
                required
              >
                {INCIDENT_TYPES.map((type) => (
                  <MenuItem key={type} value={type.toLowerCase().replace(/\s+/g, "-")}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Primary Ranger ID"
                value={backdatedDialog.primaryRangerId}
                onChange={handleBackdatedFieldChange("primaryRangerId")}
                fullWidth
                placeholder="e.g. 121"
              />

              <TextField
                label="Start Time (When incident occurred)"
                type="datetime-local"
                value={backdatedDialog.occurredAtUtc}
                onChange={handleBackdatedFieldChange("occurredAtUtc")}
                InputLabelProps={{ shrink: true }}
                fullWidth
                required
                helperText="This must be a past date/time"
              />

              <TextField
                label="End Time"
                type="datetime-local"
                value={backdatedDialog.occurredEndUtc}
                onChange={handleBackdatedFieldChange("occurredEndUtc")}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />

              <TextField
                select
                SelectProps={{ multiple: true }}
                label="Services Provided"
                value={backdatedDialog.servicesProvided}
                onChange={handleBackdatedServicesChange}
                fullWidth
              >
                {SERVICE_OPTIONS.map((service) => (
                  <MenuItem key={service} value={service}>
                    {service}
                  </MenuItem>
                ))}
              </TextField>

              <Stack direction="row" spacing={2}>
                <TextField
                  label="Latitude"
                  value={backdatedDialog.latitude}
                  onChange={handleBackdatedFieldChange("latitude")}
                  fullWidth
                  placeholder="e.g. 32.916199"
                  type="number"
                  inputProps={{ step: "any" }}
                />
                <TextField
                  label="Longitude"
                  value={backdatedDialog.longitude}
                  onChange={handleBackdatedFieldChange("longitude")}
                  fullWidth
                  placeholder="e.g. -96.575505"
                  type="number"
                  inputProps={{ step: "any" }}
                />
              </Stack>

              <TextField
                label="Location Description (optional)"
                value={backdatedDialog.locationDescription}
                onChange={handleBackdatedFieldChange("locationDescription")}
                fullWidth
                placeholder="e.g. I-35E NB at Exit 445"
              />

              <TextField
                label="Notes"
                value={backdatedDialog.notes}
                onChange={handleBackdatedFieldChange("notes")}
                multiline
                rows={3}
                fullWidth
              />

              {backdatedDialog.error && (
                <Alert severity="error">{backdatedDialog.error}</Alert>
              )}
              {backdatedDialog.success && (
                <Alert severity="success">{backdatedDialog.success}</Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => closeBackdatedDialog()} disabled={backdatedDialog.submitting}>
              {backdatedDialog.success ? "Close" : "Cancel"}
            </Button>
            {!backdatedDialog.success && (
              <Button
                variant="contained"
                onClick={handleConfirmBackdated}
                disabled={backdatedDialog.submitting}
              >
                {backdatedDialog.submitting ? "Creating…" : "Create Backdated Incident"}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Video Viewer Dialog */}
        <Dialog
          open={videoDialog.open}
          onClose={closeVideoDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Videos for Incident #{videoDialog.incident?.cadId}
          </DialogTitle>
          <DialogContent>
            {videoDialog.videos?.length > 0 ? (
              <Stack spacing={2}>
                {/* Video Player */}
                {videoDialog.selectedVideoId && (
                  <Box sx={{ position: "relative", width: "100%", bgcolor: "black", borderRadius: 1 }}>
                    <video
                      key={videoDialog.selectedVideoId}
                      controls
                      autoPlay
                      style={{ width: "100%", maxHeight: "400px" }}
                      src={buildSecuredUrl(`/api/videos/${videoDialog.selectedVideoId}/stream`)}
                    >
                      Your browser does not support the video tag.
                    </video>
                    {/* Delete button for admins */}
                    {user?.role === "admin" && (
                      <Box sx={{ position: "absolute", top: 8, right: 8 }}>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={() => openDeleteVideoDialog(videoDialog.videos.find(v => v.id === videoDialog.selectedVideoId))}
                        >
                          Delete Video
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Video Selection Chips */}
                {videoDialog.videos?.length > 1 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Select Video:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {videoDialog.videos.map((video, idx) => (
                        <Chip
                          key={video.id}
                          label={`Video ${idx + 1}`}
                          onClick={() => handleSelectVideo(video.id)}
                          color={videoDialog.selectedVideoId === video.id ? "primary" : "default"}
                          variant={videoDialog.selectedVideoId === video.id ? "filled" : "outlined"}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Video Info */}
                {videoDialog.selectedVideoId && (() => {
                  const selectedVideo = videoDialog.videos.find(v => v.id === videoDialog.selectedVideoId);
                  if (!selectedVideo) return null;
                  return (
                    <Box sx={{ p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
                      <Typography variant="body2">
                        <strong>Recorded:</strong>{" "}
                        {new Date(selectedVideo.timestamp).toLocaleString()}
                      </Typography>
                      {selectedVideo.rangerId && (
                        <Typography variant="body2">
                          <strong>Ranger:</strong> {selectedVideo.rangerId}
                        </Typography>
                      )}
                      {selectedVideo.cameraType && (
                        <Typography variant="body2">
                          <strong>Camera:</strong> {selectedVideo.cameraType}
                        </Typography>
                      )}
                    </Box>
                  );
                })()}
              </Stack>
            ) : (
              <Typography>No videos available for this incident.</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeVideoDialog}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Delete Video Confirmation Dialog */}
        <Dialog
          open={deleteVideoDialog.open}
          onClose={closeDeleteVideoDialog}
        >
          <DialogTitle>Delete Video?</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this video? This action cannot be undone.
            </Typography>
            {deleteVideoDialog.error && (
              <Alert severity="error" sx={{ mt: 2 }}>{deleteVideoDialog.error}</Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDeleteVideoDialog} disabled={deleteVideoDialog.submitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDeleteVideo}
              disabled={deleteVideoDialog.submitting}
            >
              {deleteVideoDialog.submitting ? "Deleting…" : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </DashboardLayout>
  );
}

export default SupervisorIncidentHistory;
